import { useState } from "react";
import { HelpCircle, X, TrendingUp, TrendingDown, Minus } from "lucide-react";

const C = {
  surface: "#0D0D12",
  border: "var(--bc-border, rgba(255,255,255,0.07))",
  text: "var(--bc-ink, #FAFAFA)",
  muted: "#6B7280",
  cyan: "#06B6D4",
  green: "#22C55E",
  yellow: "#F59E0B",
  red: "#EF4444",
  purple: "#A78BFA",
  orange: "#F97316",
};

const NEON_CSS = `
  @keyframes sigma-pulse-cyan {
    0%,100% { opacity:0.55; box-shadow:0 0 6px rgba(6,182,212,0.4); }
    50%      { opacity:0.85; box-shadow:0 0 18px rgba(6,182,212,0.7),0 0 32px rgba(6,182,212,0.25); }
  }
  @keyframes sigma-pulse-green {
    0%,100% { opacity:0.55; box-shadow:0 0 6px rgba(34,197,94,0.4); }
    50%      { opacity:0.85; box-shadow:0 0 18px rgba(34,197,94,0.7),0 0 32px rgba(34,197,94,0.25); }
  }
  @keyframes sigma-pulse-amber {
    0%,100% { opacity:0.55; box-shadow:0 0 6px rgba(245,158,11,0.4); }
    50%      { opacity:0.85; box-shadow:0 0 18px rgba(245,158,11,0.7),0 0 32px rgba(245,158,11,0.25); }
  }
  @keyframes sigma-pulse-purple {
    0%,100% { opacity:0.55; box-shadow:0 0 6px rgba(167,139,250,0.4); }
    50%      { opacity:0.85; box-shadow:0 0 18px rgba(167,139,250,0.7),0 0 32px rgba(167,139,250,0.25); }
  }
  .sneon-cyan   { animation:sigma-pulse-cyan   2.4s ease-in-out infinite; }
  .sneon-green  { animation:sigma-pulse-green  2.4s ease-in-out infinite 0.6s; }
  .sneon-amber  { animation:sigma-pulse-amber  2.4s ease-in-out infinite 1.2s; }
  .sneon-purple { animation:sigma-pulse-purple 2.4s ease-in-out infinite 1.8s; }
`;

const EXPLANATIONS = [
  {
    name: "Tendenza Statistica",
    color: C.cyan,
    desc: "Confronta l'attacco di ogni squadra con la difesa avversaria. Un valore positivo significa che una squadra attacca meglio di quanto l'altra difenda. Ti dice chi ha il vantaggio statistico reale in questa partita.",
  },
  {
    name: "Elm's Factor",
    color: C.green,
    desc: "Misura la 'solidità difensiva' complessiva della partita. Più è alto, più è probabile che ci siano pochi gol e clean sheet. Basso (sotto 35%) significa che entrambi gli attacchi potrebbero segnare.",
  },
  {
    name: "Inclinazione",
    color: C.yellow,
    desc: "Mostra chi si aspetta di avere più possesso e pericoli offensivi. Sopra 55% vuol dire che la squadra di casa dovrebbe dominare il gioco. Sotto 45% indica dominio ospite. Utile per mercati come 'tiri in porta' o 'angoli'.",
  },
  {
    name: "Stabilità",
    color: C.purple,
    desc: "Indica quanto le prestazioni attuali delle squadre siano sostenibili rispetto alle loro medie storiche. Valori alti (oltre 130%) segnalano che il modello calcola un xG molto superiore ai gol storici — possibile instabilità nei pronostici.",
  },
];

function generateInterpretation(metrics, homeTeam, awayTeam) {
  if (!metrics || Object.keys(metrics).length === 0) return null;
  const { tendenza, elms_factor, inclinazione, tension_trap } = metrics;
  const parts = [];

  // --- Tendenza Statistica ---
  if (tendenza) {
    const { home: h, away: a, dom } = tendenza;
    const diff = Math.abs(h - a);
    if (dom === "home") {
      if (diff > 0.8) {
        parts.push(`Statisticamente ${homeTeam} attacca molto meglio di quanto ${awayTeam} difende: c'è un vantaggio netto per la squadra di casa.`);
      } else {
        parts.push(`${homeTeam} ha un piccolo vantaggio statistico rispetto agli ospiti, ma la differenza non è marcata.`);
      }
    } else if (dom === "away") {
      if (diff > 0.8) {
        parts.push(`I numeri favoriscono chiaramente ${awayTeam}: la loro forza offensiva supera la difesa avversaria.`);
      } else {
        parts.push(`${awayTeam} è leggermente avvantaggiata secondo i dati stagionali, senza però un divario netto.`);
      }
    } else {
      parts.push(`Le due squadre si equivalgono: nessuna ha un vantaggio statistico evidente sull'altra.`);
    }
  }

  // --- Elm's Factor → cosa aspettarsi sui gol ---
  if (elms_factor !== null && elms_factor !== undefined) {
    if (elms_factor >= 65) {
      parts.push(`Entrambe le difese sono solide: ci si aspetta pochi gol, con buone probabilità che almeno una delle due squadre non subisca reti. Mercati consigliati: Under 2.5 o No Goal.`);
    } else if (elms_factor >= 40) {
      parts.push(`La partita è incerta sui gol: possibile che segnino entrambe le squadre, ma senza una tendenza netta. Valuta sia Over che Under con cautela.`);
    } else {
      parts.push(`Le difese lasciano spazio: è probabile che vengano segnati almeno 2-3 gol. I mercati Over 1.5 o Goal (entrambe segnano) potrebbero essere interessanti.`);
    }
  }

  // --- Inclinazione → chi gestisce il gioco ---
  if (inclinazione !== null && inclinazione !== undefined) {
    if (inclinazione >= 65) {
      parts.push(`${homeTeam} dovrebbe avere il controllo del gioco per la maggior parte della partita.`);
    } else if (inclinazione <= 35) {
      parts.push(`${awayTeam} è attesa come squadra dominante nel gioco, nonostante giochi in trasferta.`);
    }
    // tra 36 e 64 è equilibrio — non serve commentarlo
  }

  // --- Stabilità → quanto fidarsi dei pronostici ---
  if (tension_trap !== null && tension_trap !== undefined) {
    if (tension_trap > 150) {
      parts.push(`⚠ Attenzione: i dati storici di queste squadre sono molto bassi rispetto a quanto il modello proietta. Questo aumenta l'incertezza — i pronostici su questa partita hanno un margine di errore più alto del solito.`);
    } else if (tension_trap > 110) {
      parts.push(`I pronostici sono basati su proiezioni leggermente superiori alle medie stagionali: considera un leggero margine di variabilità.`);
    } else if (tension_trap < 85) {
      parts.push(`Le squadre stanno producendo meno di quanto il loro potenziale suggerirebbe — possibile rimonta nei prossimi turni.`);
    }
    // tra 85 e 110 è stabile — non serve commentarlo
  }

  return parts.length > 0 ? parts.join(" ") : null;
}

function NeonBar({ pct, colorClass, color }) {
  return (
    <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bc-border, rgba(255,255,255,0.07))" }}>
      <div className={`h-full rounded-full ${colorClass}`}
        style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }} />
    </div>
  );
}

function TendenzaCard({ data, homeTeam, awayTeam }) {
  if (!data) return null;
  const { home, away, dom } = data;
  const homeColor = home > 0 ? C.cyan : home < 0 ? C.red : C.muted;
  const awayColor = away > 0 ? C.cyan : away < 0 ? C.red : C.muted;
  const DomIcon = dom === "home" ? TrendingUp : dom === "away" ? TrendingDown : Minus;
  const domColor = dom === "home" ? C.cyan : dom === "away" ? C.orange : C.muted;
  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center justify-between">
        <span className="font-data text-xs" style={{ color: C.muted }}>Casa</span>
        <span className="font-data font-bold text-base tabular-nums" style={{ color: homeColor }}>
          {home > 0 ? "+" : ""}{home}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="font-data text-xs" style={{ color: C.muted }}>Ospiti</span>
        <span className="font-data font-bold text-base tabular-nums" style={{ color: awayColor }}>
          {away > 0 ? "+" : ""}{away}
        </span>
      </div>
      <div className="flex items-center gap-1.5 mt-1 pt-1.5" style={{ borderTop: `1px solid ${C.border}` }}>
        <DomIcon size={10} style={{ color: domColor }} />
        <span className="font-data text-[10px] uppercase tracking-wider truncate" style={{ color: domColor }}>
          {dom === "home" ? (homeTeam || "Casa") : dom === "away" ? (awayTeam || "Ospiti") : "Equilibrio"}
        </span>
      </div>
    </div>
  );
}

function ElmsCard({ value }) {
  if (value === null || value === undefined) return <span style={{ color: C.muted }}>—</span>;
  const color = value >= 60 ? C.green : value >= 35 ? C.yellow : C.red;
  // "Mista" → "Incerta"
  const label = value >= 60 ? "CHIUSA" : value >= 35 ? "INCERTA" : "APERTA";
  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-end justify-between">
        <span className="font-data font-black tabular-nums" style={{ color, fontSize: "1.6rem", lineHeight: 1 }}>{value}%</span>
        <span className="font-data text-[10px] uppercase tracking-widest px-1.5 py-0.5"
          style={{ color, background: `${color}18`, border: `1px solid ${color}30` }}>{label}</span>
      </div>
      <NeonBar pct={value} colorClass="sneon-green" color={color} />
    </div>
  );
}

function InclinazioneCard({ value, homeTeam, awayTeam }) {
  if (value === null || value === undefined) return <span style={{ color: C.muted }}>—</span>;
  const awayPct = Math.round((100 - value) * 10) / 10;
  const homeRounded = Math.round(value * 10) / 10;
  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center justify-between mb-0.5">
        <span className="font-data text-[10px]" style={{ color: C.muted }}>Casa</span>
        <span className="font-data text-[10px]" style={{ color: C.muted }}>Ospiti</span>
      </div>
      <div className="w-full h-2 rounded-full overflow-hidden flex" style={{ background: "var(--bc-border, rgba(255,255,255,0.07))" }}>
        <div className="sneon-amber h-full transition-all" style={{ width: `${homeRounded}%`, background: C.yellow }} />
        <div className="h-full transition-all" style={{ width: `${awayPct}%`, background: "rgba(239,68,68,0.4)" }} />
      </div>
      <div className="flex items-center justify-between">
        <span className="font-data font-bold tabular-nums" style={{ color: C.yellow, fontSize: "1.1rem" }}>{homeRounded}%</span>
        <span className="font-data font-bold tabular-nums" style={{ color: "rgba(239,68,68,0.9)", fontSize: "1.1rem" }}>{awayPct}%</span>
      </div>
    </div>
  );
}

function StabilitaCard({ value }) {
  if (value === null || value === undefined) return <span style={{ color: C.muted }}>—</span>;
  const color = value > 130 ? C.red : value < 90 ? C.purple : C.green;
  const label = value > 150 ? "INSTABILE" : value > 130 ? "VARIABILE" : value < 90 ? "SOTTO-MEDIA" : "STABILE";
  const displayVal = value > 999 ? ">999" : value;
  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-end justify-between">
        <span className="font-data font-black tabular-nums" style={{ color, fontSize: "1.6rem", lineHeight: 1 }}>{displayVal}%</span>
        <span className="font-data text-[10px] uppercase tracking-widest px-1.5 py-0.5"
          style={{ color, background: `${color}18`, border: `1px solid ${color}30` }}>{label}</span>
      </div>
      <NeonBar pct={Math.min(value, 300) / 3} colorClass="sneon-purple" color={color} />
    </div>
  );
}

export default function AdvancedMetricsRow({ metrics, homeTeam, awayTeam }) {
  const [showInfo, setShowInfo] = useState(false);

  if (!metrics || Object.keys(metrics).length === 0) return null;

  const interpretation = generateInterpretation(metrics, homeTeam, awayTeam);

  const cards = [
    {
      label: "Tendenza Statistica",
      color: C.cyan,
      content: <TendenzaCard data={metrics.tendenza} homeTeam={homeTeam} awayTeam={awayTeam} />,
    },
    {
      label: "Elm's Factor",
      color: C.green,
      content: <ElmsCard value={metrics.elms_factor} />,
    },
    {
      label: "Inclinazione",
      color: C.yellow,
      content: <InclinazioneCard value={metrics.inclinazione} homeTeam={homeTeam} awayTeam={awayTeam} />,
    },
    {
      label: "Stabilità",
      color: C.purple,
      content: <StabilitaCard value={metrics.tension_trap} />,
    },
  ];

  return (
    <>
      <style>{NEON_CSS}</style>

      <div className="space-y-2" data-testid="advanced-metrics-row">
        {/* Header */}
        <div className="flex items-center justify-between px-1">
          <span className="font-heading font-black text-[10px] uppercase tracking-[0.2em]" style={{ color: C.muted }}>
            ANALISI AVANZATA
          </span>
          <button
            onClick={() => setShowInfo(true)}
            data-testid="advanced-metrics-info-btn"
            className="flex items-center gap-1 px-2 py-0.5 transition-opacity hover:opacity-80"
            style={{ color: C.muted, background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))", border: `1px solid ${C.border}` }}
          >
            <HelpCircle size={11} />
            <span className="font-data text-[10px]">Legenda</span>
          </button>
        </div>

        {/* 4-card grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {cards.map((card) => (
            <div
              key={card.label}
              className="p-3 flex flex-col gap-2"
              style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderTop: `2px solid ${card.color}`,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div style={{
                position: "absolute", inset: 0, pointerEvents: "none",
                background: `radial-gradient(ellipse at 50% 0%, ${card.color}08 0%, transparent 70%)`,
              }} />
              <span className="font-heading font-black text-[9px] uppercase tracking-[0.15em] relative" style={{ color: card.color }}>
                {card.label}
              </span>
              <div className="relative">{card.content}</div>
            </div>
          ))}
        </div>

        {/* Interpretation box */}
        {interpretation && (
          <div
            className="px-3 py-2.5"
            style={{
              background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))",
              border: `1px solid ${C.border}`,
              borderLeft: `3px solid rgba(99,102,241,0.5)`,
            }}
          >
            <p className="font-data text-xs leading-relaxed" style={{ color: "rgba(250,250,250,0.65)" }}>
              {interpretation}
            </p>
          </div>
        )}
      </div>

      {/* Info Modal */}
      {showInfo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "var(--bc-panel, rgba(0,0,0,0.85))", backdropFilter: "blur(6px)" }}
          onClick={() => setShowInfo(false)}
        >
          <div
            className="w-full max-w-md animate-fade-in"
            style={{ background: "#0D0D12", border: `1px solid var(--bc-border, rgba(255,255,255,0.1))` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4"
              style={{ borderBottom: `1px solid var(--bc-border, rgba(255,255,255,0.07))` }}>
              <span className="font-heading font-black text-sm uppercase tracking-widest" style={{ color: C.text }}>
                Analisi Avanzata — Legenda
              </span>
              <button onClick={() => setShowInfo(false)} className="hover:opacity-70 transition-opacity">
                <X size={16} style={{ color: C.muted }} />
              </button>
            </div>
            <div className="p-5 space-y-5">
              {EXPLANATIONS.map((e) => (
                <div key={e.name} className="space-y-1">
                  <span className="font-heading font-black text-xs uppercase tracking-wider" style={{ color: e.color }}>
                    {e.name}
                  </span>
                  <p className="font-data text-xs leading-relaxed" style={{ color: C.muted }}>{e.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
